import React from 'react'
import { FaBuilding, FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock, FaUsers, FaShieldAlt, FaTruck, FaCreditCard, FaTimes, FaCheck } from 'react-icons/fa'

const ManufacturerBusinessInfo = ({ manufacturer, businessForm }) => {
  const formatAddress = (address) => {
    if (!address) return 'Not provided'
    return `${address.street || ''}, ${address.city || ''}, ${address.state || ''} ${address.zipCode || ''}, ${address.country || ''}`.replace(/^,\s*/, '').replace(/,\s*,/g, ',')
  }


  const formatContacts = (contacts) => {
    if (!contacts) return []
    return Object.entries(contacts).map(([type, contactList]) => ({
      type: type.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
      contacts: Array.isArray(contactList) ? contactList : []
    }))
  }

  if (!businessForm) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-[24px] md:text-[32px] font-semibold text-[var(--Dark-Blue---Primary,#2b364b)] font-barlowcondensed uppercase mb-6">
          Business Information
        </h2>
        <div className="text-center py-8">
          <p className="text-gray-500 font-montserrat">No business information available</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <h2 className="text-[24px] md:text-[32px] font-semibold text-[var(--Dark-Blue---Primary,#2b364b)] font-barlowcondensed uppercase mb-6">
        Business Information
      </h2>

      {/* Company Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-gray-50 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <FaBuilding className="text-[#2b364b] text-xl mr-3" />
            <h3 className="text-xl font-semibold text-[#2b364b] font-montserrat">Company Details</h3>
          </div>
          {businessForm && businessForm.branding && (
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-600 font-montserrat">Company Logo</label>
              <div className="mt-1 flex items-center gap-3">
                <img
                  src={businessForm?.branding?.logoUrl}
                  alt={`${businessForm.brand || businessForm.companyName || 'Logo'}`}
                  className="h-16 w-auto object-contain"
                />
              </div>
            </div>
          )}
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-600 font-montserrat">Company Name</label>
              <p className="text-[#2b364b] font-montserrat">{businessForm.companyName || 'Not provided'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 font-montserrat">Legal Name</label>
              <p className="text-[#2b364b] font-montserrat">{businessForm.legalName || 'Not provided'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 font-montserrat">Brand</label>
              <p className="text-[#2b364b] font-montserrat">{manufacturer.manufacturerObject?.brand || 'Not provided'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 font-montserrat">Subsidiary Brands</label>
              <p className="text-[#2b364b] font-montserrat">
                {businessForm.subsidiaryBrands?.length > 0 ? businessForm.subsidiaryBrands.join(', ') : 'None'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <FaEnvelope className="text-[#2b364b] text-xl mr-3" />
            <h3 className="text-xl font-semibold text-[#2b364b] font-montserrat">Contact Information</h3>
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-600 font-montserrat">Email</label>
              <p className="text-[#2b364b] font-montserrat">{businessForm.companyEmail || manufacturer.email || 'Not provided'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 font-montserrat">Phone</label>
              <p className="text-[#2b364b] font-montserrat">{businessForm.phone || businessForm.officePhoneNumber || 'Not provided'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Addresses */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-gray-50 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <FaMapMarkerAlt className="text-[#2b364b] text-xl mr-3" />
            <h3 className="text-xl font-semibold text-[#2b364b] font-montserrat">Company Address</h3>
          </div>
          <p className="text-[#2b364b] font-montserrat">{formatAddress(businessForm.companyAddress)}</p>
        </div>

        <div className="bg-gray-50 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <FaMapMarkerAlt className="text-[#2b364b] text-xl mr-3" />
            <h3 className="text-xl font-semibold text-[#2b364b] font-montserrat">Legal Business Address</h3>
          </div>
          <p className="text-[#2b364b] font-montserrat">{formatAddress(businessForm.legalBusinessAddress)}</p>
        </div>
      </div>

      {/* Hours of Operation */}
      <div className="bg-gray-50 rounded-lg p-6 mb-8">
        <div className="flex items-center mb-4">
          <FaClock className="text-[#2b364b] text-xl mr-3" />
          <h3 className="text-xl font-semibold text-[#2b364b] font-montserrat">Hours of Operation</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {businessForm.hoursOfOperation && Object.entries(businessForm.hoursOfOperation).map(([day, hours]) => (
            <div key={day} className="flex justify-between items-center p-3 bg-white rounded border">
              <span className="font-medium text-[#2b364b] font-montserrat capitalize">{day}</span>
              <span className="text-sm text-gray-600 font-montserrat">
                {hours.closed ? 'Closed' : `${hours.open || ''} - ${hours.close || ''}`}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Saddle Types */}
      <div className="bg-gray-50 rounded-lg p-6 mb-8">
        <div className="flex items-center mb-4">
          <FaShieldAlt className="text-[#2b364b] text-xl mr-3" />
          <h3 className="text-xl font-semibold text-[#2b364b] font-montserrat">Saddle Types</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {businessForm.saddleTypes?.map((type, index) => (
            <span key={index} className="bg-[#2b364b] text-white px-3 py-1 rounded-full text-sm font-montserrat">
              {type}
            </span>
          ))}
          {businessForm.otherSaddleType && (
            <span className="bg-[#5c80b6] text-white px-3 py-1 rounded-full text-sm font-montserrat">
              {businessForm.otherSaddleType}
            </span>
          )}
        </div>
      </div>

      {/* Lead Times */}
      <div className="bg-gray-50 rounded-lg p-6 mb-8">
        <div className="flex items-center mb-4">
          <FaClock className="text-[#2b364b] text-xl mr-3" />
          <h3 className="text-xl font-semibold text-[#2b364b] font-montserrat">Lead Times</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white p-4 rounded border">
            <label className="text-sm font-medium text-gray-600 font-montserrat">Production</label>
            <p className="text-[#2b364b] font-montserrat font-semibold">{businessForm.leadTimes?.production || 'Not specified'}</p>
          </div>
          <div className="bg-white p-4 rounded border">
            <label className="text-sm font-medium text-gray-600 font-montserrat">Custom</label>
            <p className="text-[#2b364b] font-montserrat font-semibold">{businessForm.leadTimes?.custom || 'Not specified'}</p>
          </div>
        </div>
      </div>

      {/* Policies */}
      <div className="bg-gray-50 rounded-lg p-6 mb-8">
        <div className="flex items-center mb-4">
          <FaShieldAlt className="text-[#2b364b] text-xl mr-3" />
          <h3 className="text-xl font-semibold text-[#2b364b] font-montserrat">Business Policies</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded border">
            <div className="flex items-center mb-2">
              <FaTruck className="text-[#2b364b] mr-2" />
              <label className="text-sm font-medium text-gray-600 font-montserrat">Shipping</label>
            </div>
            <p className="text-[#2b364b] font-montserrat">{businessForm.policies?.shipsQuickly || 'Not specified'}</p>
          </div>
          <div className="bg-white p-4 rounded border">
            <div className="flex items-center mb-2">
              <FaCreditCard className="text-[#2b364b] mr-2" />
              <label className="text-sm font-medium text-gray-600 font-montserrat">Returns</label>
            </div>
            <p className="text-[#2b364b] font-montserrat">{businessForm.policies?.acceptsReturns || 'Not specified'}</p>
            {businessForm.policies?.returnsTerms && (
              <p className="text-sm text-gray-600 font-montserrat mt-1">{businessForm.policies.returnsTerms}</p>
            )}
          </div>
          <div className="bg-white p-4 rounded border">
            <div className="flex items-center mb-2">
              <FaShieldAlt className="text-[#2b364b] mr-2" />
              <label className="text-sm font-medium text-gray-600 font-montserrat">Warranty</label>
            </div>
            <p className="text-[#2b364b] font-montserrat">{businessForm.policies?.hasWarranty || 'Not specified'}</p>
            {businessForm.policies?.warrantyTerms && (
              <p className="text-sm text-gray-600 font-montserrat mt-1">{businessForm.policies.warrantyTerms}</p>
            )}
          </div>
        </div>
      </div>

      {/* Contacts */}
      {businessForm.contacts && (
        <div className="bg-gray-50 rounded-lg p-6 mb-8">
          <div className="flex items-center mb-4">
            <FaUsers className="text-[#2b364b] text-xl mr-3" />
            <h3 className="text-xl font-semibold text-[#2b364b] font-montserrat">Contact Persons</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {formatContacts(businessForm.contacts).map((contactType) => (
              <div key={contactType.type} className="bg-white rounded-lg p-4 border">
                <h4 className="font-semibold text-[#2b364b] font-montserrat mb-3">{contactType.type}</h4>
                {contactType.contacts.length > 0 ? (
                  contactType.contacts.map((contact, index) => (
                    <div key={index} className="mb-3 p-3 bg-gray-50 rounded">
                      <p className="font-medium text-[#2b364b] font-montserrat">
                        {contact.firstName} {contact.lastName}
                      </p>
                      <p className="text-sm text-gray-600 font-montserrat">{contact.title}</p>
                      <p className="text-sm text-gray-600 font-montserrat">{contact.email}</p>
                      <p className="text-sm text-gray-600 font-montserrat">{contact.phone}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 font-montserrat">No contacts specified</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Shipping Addresses */}
      {businessForm.shippingAddress && (
        <div className="bg-gray-50 rounded-lg p-6 mb-8">
          <div className="flex items-center mb-4">
            <FaTruck className="text-[#2b364b] text-xl mr-3" />
            <h3 className="text-xl font-semibold text-[#2b364b] font-montserrat">Shipping Addresses</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded border">
              <h4 className="font-semibold text-[#2b364b] font-montserrat mb-2">Ship To</h4>
              <p className="text-[#2b364b] font-montserrat">{formatAddress(businessForm.shippingAddress.shipTo)}</p>
            </div>
            <div className="bg-white p-4 rounded border">
              <h4 className="font-semibold text-[#2b364b] font-montserrat mb-2">Ship From</h4>
              <p className="text-[#2b364b] font-montserrat">{formatAddress(businessForm.shippingAddress.shipFrom)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Connection Status */}
      <div className="bg-gray-50 rounded-lg p-6">
        <div className="flex items-center mb-4">
          <FaShieldAlt className="text-[#2b364b] text-xl mr-3" />
          <h3 className="text-xl font-semibold text-[#2b364b] font-montserrat">Integration Status</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-4 bg-white rounded border">
            <span className="font-medium text-[#2b364b] font-montserrat">Stripe Connected</span>
            <div className="flex items-center">
              {manufacturer.manufacturerObject?.isStripeConnected ? (
                <FaCheck className="text-green-500 text-xl" />
              ) : (
                <FaTimes className="text-red-500 text-xl" />
              )}
            </div>
          </div>
          <div className="flex items-center justify-between p-4 bg-white rounded border">
            <span className="font-medium text-[#2b364b] font-montserrat">Shopify Connected</span>
            <div className="flex items-center">
              {manufacturer.manufacturerObject?.isShopifyConnected ? (
                <FaCheck className="text-green-500 text-xl" />
              ) : (
                <FaTimes className="text-red-500 text-xl" />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ManufacturerBusinessInfo
